import React, { useState } from 'react';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiSend,
  FiCheck,
  FiInstagram,
  FiFacebook,
  FiTwitter,
  FiUser,
  FiMessageSquare,
} from 'react-icons/fi';
import './ContactPage.css';

const contactInfo = [
  {
    icon: <FiMail />,
    label: 'Email Us',
    value: 'info@shammyspices.com',
    href: 'mailto:info@shammyspices.com',
    note: 'We reply within 24 hours',
  },
  {
    icon: <FiPhone />,
    label: 'Call Us',
    value: '(+255) 655 778 989',
    href: 'tel:+255655778989',
    note: 'Mon – Sun, 9AM – 6PM EAT',
  },
  {
    icon: <FiMapPin />,
    label: 'Visit Us',
    value: 'Dar es Salaam, Tanzania',
    href: null,
    note: 'Open to wholesale inquiries',
  },
  {
    icon: <FiClock />,
    label: 'Business Hours',
    value: 'Monday – Sunday',
    href: null,
    note: '9:00 AM – 6:00 PM (EAT)',
  },
];

const faqItems = [
  {
    q: 'How long does shipping take?',
    a: 'Domestic orders arrive within 3–5 business days. International shipping typically takes 7–14 days.',
  },
  {
    q: 'Are your spices organic?',
    a: 'Many of our spices are certified organic. Look for the "Organic" badge on the product page.',
  },
  {
    q: 'Do you offer wholesale pricing?',
    a: 'Yes, we have wholesale programs for restaurants and retailers. Reach out via the form below.',
  },
  {
    q: 'What is your return policy?',
    a: 'We offer a 30-day hassle-free return on unopened products. Contact us to initiate a return.',
  },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-content">
          <div className="contact-eyebrow">
            <span className="eyebrow-line-ct" />
            <span>We'd Love to Hear from You</span>
            <span className="eyebrow-line-ct" />
          </div>
          <h1 className="contact-hero-title">
            Get in <span className="contact-title-accent">Touch</span>
          </h1>
          <p className="contact-hero-subtitle">
            Have a question about our spices, an order, or a wholesale inquiry? We're here.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="contact-info-section">
        <div className="contact-info-grid">
          {contactInfo.map((item, i) => (
            <div key={i} className="contact-info-card">
              <div className="contact-info-icon">{item.icon}</div>
              <div className="contact-info-body">
                <span className="contact-info-label">{item.label}</span>
                {item.href ? (
                  <a href={item.href} className="contact-info-value">
                    {item.value}
                  </a>
                ) : (
                  <span className="contact-info-value">{item.value}</span>
                )}
                <span className="contact-info-note">{item.note}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Form + FAQ */}
      <section className="contact-body">
        <div className="contact-layout">
          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <h2 className="form-section-title">Send Us a Message</h2>
            <p className="form-section-subtitle">
              Fill out the form and our team will get back to you within one business day.
            </p>

            {submitted ? (
              <div className="form-success">
                <div className="success-icon">
                  <FiCheck />
                </div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll be in touch soon.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}>
                  Send Another
                </button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <div className="input-wrapper">
                      <FiUser className="input-icon" />
                      <input
                        id="name"
                        type="text"
                        name="name"
                        placeholder="Your name"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <FiMail className="input-icon" />
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        value={form.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <div className="input-wrapper">
                    <FiMessageSquare className="input-icon" />
                    <input
                      id="subject"
                      type="text"
                      name="subject"
                      placeholder="What's this about?"
                      value={form.subject}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us how we can help..."
                    value={form.message}
                    onChange={handleChange}
                    rows={6}
                    required
                  />
                </div>

                <button type="submit" className="submit-btn">
                  <span>Send Message</span>
                  <FiSend />
                </button>
              </form>
            )}
          </div>

          {/* FAQ Panel */}
          <div className="contact-faq">
            <h2 className="faq-title">Frequently Asked</h2>
            <div className="faq-list">
              {faqItems.map((item, i) => (
                <div
                  key={i}
                  className={`faq-item ${openFaq === i ? 'open' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                  >
                    <span>{item.q}</span>
                    <span className="faq-icon">{openFaq === i ? '−' : '+'}</span>
                  </button>
                  {openFaq === i && (
                    <div className="faq-answer">
                      <p>{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="contact-social">
              <p className="social-label">Follow our journey</p>
              <div className="social-row">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="contact-social-link">
                  <FiInstagram />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="contact-social-link">
                  <FiFacebook />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="contact-social-link">
                  <FiTwitter />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
